// src/utils/storeBinding.js
// Robust Neo device binding persistence.
// Mobile installed PWAs can reopen on /link and sometimes read localStorage late/stale.
// Keep the device link mirrored in localStorage, sessionStorage and a first-party cookie,
// then heal localStorage before guards decide whether the device is linked.

export const BIND_KEYS = {
  orgId: "orgId",
  storeId: "storeId",
  storeName: "storeName",
  orgName: "orgName",
  deviceUid: "deviceUid",
  deviceType: "deviceType",
  businessMode: "businessMode",
  appVariant: "appVariant",
  boundAt: "boundAt",
  recoverySecret: "recoverySecret",
};

const BINDING_JSON_KEY = "neo.deviceBinding.v2";
const COOKIE_KEY = "neoDeviceBinding";

function safeWindow() {
  return typeof window !== "undefined" ? window : null;
}

function safeStorage(storageName) {
  try {
    const w = safeWindow();
    const s = w?.[storageName];
    if (!s) return null;
    const testKey = "__neo_storage_test__";
    s.setItem(testKey, "1");
    s.removeItem(testKey);
    return s;
  } catch {
    return null;
  }
}

function readJSON(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function cleanBinding(raw = {}) {
  const out = {};
  Object.values(BIND_KEYS).forEach((key) => {
    const v = raw?.[key];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      out[key] = String(v);
    }
  });
  if (!out.boundAt) out.boundAt = String(Date.now());
  return out;
}

function readCookieBinding() {
  try {
    if (typeof document === "undefined") return null;
    const item = String(document.cookie || "")
      .split(";")
      .map((x) => x.trim())
      .find((x) => x.startsWith(`${COOKIE_KEY}=`));
    if (!item) return null;
    return readJSON(decodeURIComponent(item.slice(COOKIE_KEY.length + 1)));
  } catch {
    return null;
  }
}

function writeCookieBinding(binding) {
  try {
    if (typeof document === "undefined") return;
    const encoded = encodeURIComponent(JSON.stringify(binding || {}));
    // 400 days is the modern browser max-ish; SameSite=Lax keeps it first-party.
    document.cookie = `${COOKIE_KEY}=${encoded}; Max-Age=34560000; Path=/; SameSite=Lax; Secure`;
  } catch {
    /* ignore */
  }
}

function clearCookieBinding() {
  try {
    if (typeof document === "undefined") return;
    document.cookie = `${COOKIE_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function readFromStorage(storageName) {
  const s = safeStorage(storageName);
  if (!s) return null;

  const fromJson = readJSON(s.getItem(BINDING_JSON_KEY));
  if (fromJson?.orgId || fromJson?.storeId) return fromJson;

  const raw = {};
  Object.values(BIND_KEYS).forEach((key) => {
    const v = s.getItem(key);
    if (v) raw[key] = v;
  });
  return raw.orgId || raw.storeId ? raw : null;
}

function writeToStorage(storageName, binding) {
  const s = safeStorage(storageName);
  if (!s) return;
  try {
    s.setItem(BINDING_JSON_KEY, JSON.stringify(binding));
    Object.entries(binding).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value) !== "") {
        s.setItem(key, String(value));
      }
    });
  } catch {
    /* ignore */
  }
}

export function saveBinding(binding = {}) {
  const current = readBinding({ heal: false });
  const incoming = cleanBinding(binding || {});

  const currentOrgId = String(current?.orgId || "").trim();
  const currentStoreId = String(current?.storeId || "").trim();
  const incomingOrgId = String(incoming?.orgId || "").trim();
  const incomingStoreId = String(incoming?.storeId || "").trim();

  const bindingChanged =
    Boolean(currentOrgId && incomingOrgId && currentOrgId !== incomingOrgId) ||
    Boolean(currentStoreId && incomingStoreId && currentStoreId !== incomingStoreId);

  // A different store is a NEW tenant context, not an update to the old one.
  // Do not inherit District Six storeName/recovery/device fields into Sea Point.
  const base = bindingChanged ? {} : (current || {});
  const clean = cleanBinding({
    ...base,
    ...(binding || {}),
    boundAt: String(Date.now()),
  });

  if (!clean.orgId || !clean.storeId) return clean;

  writeToStorage("localStorage", clean);
  writeToStorage("sessionStorage", clean);
  writeCookieBinding(clean);

  try {
    window.dispatchEvent(
      new CustomEvent("neo:binding-changed", { detail: { ...clean } })
    );
  } catch {}

  return clean;
}

export function readBinding(options = {}) {
  const { heal = true } = options;
  const legacyLastLinked = (() => {
    try {
      const s = safeStorage("localStorage");
      return readJSON(s?.getItem("neo.lastLinkedBinding"));
    } catch {
      return null;
    }
  })();

  const candidates = [
    readFromStorage("localStorage"),
    readFromStorage("sessionStorage"),
    readCookieBinding(),
    legacyLastLinked,
  ].filter(Boolean);

  const best =
    candidates.find((x) => x?.orgId && x?.storeId) ||
    candidates.find((x) => x?.storeId) ||
    candidates[0] ||
    {};

  const clean = cleanBinding(best);
  if (heal && (clean.orgId || clean.storeId)) {
    writeToStorage("localStorage", clean);
    writeToStorage("sessionStorage", clean);
    writeCookieBinding(clean);
  }
  return clean;
}

export function isBound() {
  const b = readBinding();
  return !!(b.orgId && b.storeId);
}

export function clearBinding() {
  const keys = [
    ...Object.values(BIND_KEYS),
    BINDING_JSON_KEY,
    "neo.lastLinkedBinding",
    "neo.lastLinkedAt",
    "linkedOrgId",
    "linkedStoreId",
    "linkedStoreName",
    "deviceLinked",
  ];

  ["localStorage", "sessionStorage"].forEach((storageName) => {
    const s = safeStorage(storageName);
    if (!s) return;
    keys.forEach((key) => {
      try { s.removeItem(key); } catch {}
    });
  });

  clearCookieBinding();
}

// Backwards-compatible aliases used by older code.
export const saveStoreBinding = saveBinding;
export const readStoreBinding = readBinding;
export const clearStoreBinding = clearBinding;
export const hasStoreBinding = isBound;
